import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { LocationForecastBlock } from './weatherForecastUtils';
import { formatLocalTimeFromOffset, weatherIconFor } from './weatherForecastUtils';

type Variant = 'sleep' | 'wallboard';

const variantStyles: Record<
  Variant,
  {
    loading: string;
    error: string;
    label: string;
    time: string;
    weekday: string;
    icon: string;
    hi: string;
    lo: string;
    divider: string;
  }
> = {
  sleep: {
    loading: 'text-sm text-white/70',
    error: 'text-xs text-white/60',
    label: 'text-xs text-white/80 font-medium leading-snug min-w-0 flex-1',
    time: 'text-xs text-white/70 tabular-nums shrink-0',
    weekday: 'text-[10px] sm:text-xs text-white/55 uppercase tracking-wide truncate w-full',
    icon: 'text-lg sm:text-xl text-sky-200 my-1',
    hi: 'text-[10px] sm:text-xs text-white/80 leading-tight tabular-nums',
    lo: 'text-white/50',
    divider: 'border-t border-white/10 pt-3 mt-1',
  },
  wallboard: {
    loading: 'text-base text-base-content/70',
    error: 'text-sm text-base-content/60',
    label: 'text-sm text-base-content font-medium leading-snug min-w-0 flex-1',
    time: 'text-sm text-base-content/70 tabular-nums shrink-0',
    weekday: 'text-xs sm:text-sm text-base-content/55 uppercase tracking-wide truncate w-full',
    icon: 'text-2xl sm:text-3xl text-primary my-2',
    hi: 'text-xs sm:text-sm text-base-content leading-tight tabular-nums',
    lo: 'text-base-content/50',
    divider: 'border-t border-base-content/10 pt-4 mt-2',
  },
};

export const WeatherForecastBlocksView: React.FC<{
  variant: Variant;
  blocks: LocationForecastBlock[] | 'loading' | null;
  nowMs: number;
}> = ({ variant, blocks, nowMs }) => {
  const s = variantStyles[variant];

  if (blocks === 'loading' || blocks === null) {
    return <div className={s.loading}>Loading weather…</div>;
  }

  return (
    <>
      {blocks.map((block, blockIdx) => (
        <div key={`${blockIdx}-${block.query}`} className={blockIdx > 0 ? s.divider : variant === 'wallboard' ? 'pt-1' : 'pt-1'}>
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className={s.label}>{block.label}</div>
            <div className={s.time}>{formatLocalTimeFromOffset(nowMs, block.tzOffsetSec)}</div>
          </div>
          {block.error ? (
            <div className={s.error}>{block.error}</div>
          ) : (
            <div className="flex gap-1 sm:gap-2 md:gap-3 justify-between">
              {block.days.map((d, i) => (
                <div key={`${d.weekdayShort}-${i}`} className="flex flex-col items-center flex-1 min-w-0 text-center">
                  <span className={s.weekday}>{d.weekdayShort}</span>
                  <FontAwesomeIcon icon={weatherIconFor(d.main, d.iconCode)} className={s.icon} title={d.main} />
                  <div className={s.hi}>
                    <div>{d.maxF}°</div>
                    <div className={s.lo}>{d.minF}°</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </>
  );
};
